import isEqual from 'lodash/isEqual';
import {makeReactiveBase, mergeRouteMeta} from '../start-ui';
import * as helper from '../helper';

function installComponents(Vue) {
    Vue.component('btn', require('../components/Btn').default);
    Vue.component('icon', require('../components/Icon').default);
    Vue.component('loading-indicator', require('../components/LoadingIndicator').default);
    Vue.component('pagination', require('../components/Pagination').default);
    Vue.component('popover', require('../components/Popover').default);
    Vue.component('hint', require('../components/Hint').default);
    Vue.component('date-time-picker', require('../components/DateTimePicker').default);
    Vue.component('time-picker', require('../components/TimePicker').default);
    Vue.component('date-picker', require('../components/DatePicker').default);
    Vue.component('checkbox', require('../components/Checkbox').default);
    Vue.component('v-select', require('../components/VSelect').default);
    Vue.component('collection', require('../components/Collection').default);
    Vue.component('text-area', require('../components/TextArea').default);
    Vue.component('hint', require('../components/Hint').default);
    Vue.component('number-input', require('../components/NumberInput').default);
    Vue.component('file-input', require('../components/FileInput').default);
    Vue.component('radio', require('../components/Radio').default);
    Vue.component('sticky', require('../components/Sticky').default);
    Vue.component('navigation', require('../components/Navigation').default);
    Vue.component('nav-item', require('../components/NavItem').default);
    Vue.component('alert', require('../components/Alert').default);
    Vue.component('toggle', require('../components/Toggle').default);
    Vue.component('v-tagging', require('../components/VTagging').default);
    Vue.component('infinite-scroll', require('../components/InfiniteScroll').default);
    Vue.component('v-table', require('../components/VTable').default);
    Vue.component('flex', require('../components/Flex').default);
    Vue.component('panel', require('../components/Panel').default);
    Vue.component('upload', require('../components/Upload').default);
    Vue.component('color-picker', require('../components/ColorPicker').default);
}

function installDirectives(Vue) {
    Vue.directive('c', require('../directives/cDirective').default);
    Vue.directive('disabled', require('../directives/disabledDirective').default);
    Vue.directive('focus', require('../directives/cDirective').default);
    Vue.directive('expand', require('../directives/cDirective').default);
}

function installFilters(Vue) {
    Vue.filter('strip', value => helper.stripTags(value));
    Vue.filter('number', (value, fixed) => helper.formatNumber(value, fixed));
}

export default {
    requiredPlugins: ['i18n'],

    defaultConfig: {
        titleTemplate: '%s',
        defaultTitle: '',
        thousandsSeparator: ' ',
        floatSeparator: ',',
    },

    install(Vue, {router = null, store = null, ...config} = {}) {
        let base = makeReactiveBase({
            data: () => ({
                titleTemplate: config.titleTemplate,
                defaultTitle: config.defaultTitle,
                documentTitle: '',
                ie11: !!window.MSInputMethodContext && !!document.documentMode,
                isTouch: "ontouchstart" in document.documentElement,
                scrollWidth: 0,
                clientWidth: 0,
                scrollHeight: 0,
                clientHeight: 0,
                scrollLeft: 0,
                scrollTop: 0,
                offsetRight: 0,
                offsetBottom: 0,
            }),
            watch: {
                documentTitle: {
                    handler() {
                        this.updateTitle();
                    },
                    immediate: true,
                },
                titleTemplate() {
                    this.updateTitle();
                },
                defaultTitle() {
                    this.updateTitle();
                },
            },
            methods: {
                updateTitle() {
                    let title = this.documentTitle;

                    if (!title) {
                        title = this.defaultTitle;
                    } else if (this.titleTemplate instanceof Function) {
                        title = this.titleTemplate(title);
                    } else {
                        title = this.titleTemplate.replace('%s', title);
                    }

                    document.title = title;
                },
            },
        });
        let ui = Object.assign(base.$data, helper);

        function updateDimensions() {
            let {scrollTop, scrollLeft, scrollWidth, clientWidth, scrollHeight, clientHeight} = document.scrollingElement || document.body.parentNode || document.body || document.documentElement; // ie11 :|
            Vue.set(ui, 'scrollTop', scrollTop);
            Vue.set(ui, 'scrollLeft', scrollLeft);
            Vue.set(ui, 'scrollWidth', scrollWidth);
            Vue.set(ui, 'clientWidth', clientWidth);
            Vue.set(ui, 'scrollHeight', scrollHeight);
            Vue.set(ui, 'clientHeight', clientHeight);
            Vue.set(ui, 'offsetRight', scrollWidth - scrollLeft - clientWidth);
            Vue.set(ui, 'offsetBottom', scrollHeight - scrollTop - clientHeight);
        }

        window.addEventListener('scroll', updateDimensions);
        window.addEventListener('resize', updateDimensions);
        setInterval(updateDimensions, 500);

        updateDimensions();

        installComponents(Vue);
        installDirectives(Vue);
        installFilters(Vue);

        if (router !== null) {
            router.beforeEach((to, from, next) => {
                next();
                helper.delay(50).then(updateDimensions);
            });

            ui.paramsToString = (params) => {
                let result = {};
                for (let key of Object.keys(params)) {
                    // parse all params to string because they are strings in this-router
                    result[key] = params[key]+"";
                }
                return result;
            };

            ui.isCurrentRoute = (name, params) => {
                let route = router.currentRoute;

                // we have to parse all params to string to avoid different types on isEqual check..
                return name === route.name && isEqual(Vue.$ui.paramsToString(params), Vue.$ui.paramsToString(route.params));
            };

            ui.pushRoute = (name, params) => {
                return router.push({name, params});
            };

            ui.replaceRoute = (name, params) => {
                return router.replace({name, params});
            };

            router.beforeEach((to, from, next) => {
                let {title = ''} = mergeRouteMeta(to);
                ui.documentTitle = title;
                next();
            });
        }

        if (store !== null) {
            store.registerModule('loading', require('../modules/loadingModule').default);
            Vue.$loading = Vue.prototype.$loading = (key, loading) => {
                if (typeof loading === "undefined") {
                    return store.getters['loading/isLoading'](key);
                }
                return store.dispatch('loading/setLoading', [key, !!loading]);
            };
        }

        Vue.$log = Vue.prototype.$log = console.log;
        Vue.$ui = Vue.prototype.$ui = ui;
    },
};
